import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarCheck2, ChevronLeft } from "lucide-react";
import {
  createPublicBooking,
  getPublicBooking,
  getPublicDates,
  getPublicSlots,
  getSchedulingErrorMessage,
} from "../api/schedulingApi";
import { formatDate, formatDateTime, formatTime } from "../components/admin/scheduling/ui";

const ANY = "any";

function Choice({ selected, onClick, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
        selected ? "border-[#096dea] bg-blue-50 ring-1 ring-[#096dea]" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
    </button>
  );
}

function Step({ title, onBack, children }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        {onBack && (
          <button type="button" onClick={onBack} className="rounded-full p-1 text-slate-500 hover:bg-slate-100" aria-label="Back">
            <ChevronLeft size={18} />
          </button>
        )}
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function PublicBookingPage() {
  const { botId } = useParams();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState("service");
  const [serviceId, setServiceId] = useState(null);
  const [providerId, setProviderId] = useState(ANY);
  const [dates, setDates] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    getPublicBooking(botId)
      .then((data) => {
        setInfo(data);
        setStep(data.services.length ? "service" : "provider");
      })
      .catch((err) => setLoadError(getSchedulingErrorMessage(err, "Online booking is not available.")));
  }, [botId]);

  const choiceParams = () => ({
    service_id: serviceId || undefined,
    provider_id: providerId === ANY ? undefined : providerId,
  });

  const goToDates = async () => {
    setError("");
    setDates(null);
    setStep("date");
    try {
      setDates(await getPublicDates(botId, choiceParams()));
    } catch (err) {
      setError(getSchedulingErrorMessage(err));
      setDates([]);
    }
  };

  const pickDate = async (d) => {
    setDate(d);
    setSlot("");
    setSlots(null);
    setError("");
    setStep("time");
    try {
      setSlots(await getPublicSlots(botId, { ...choiceParams(), date: d }));
    } catch (err) {
      setError(getSchedulingErrorMessage(err));
      setSlots([]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const result = await createPublicBooking(botId, {
        start_at: slot,
        service_id: serviceId,
        provider_id: providerId === ANY ? null : providerId,
        name: form.name.trim(),
        phone: form.phone.replace(/[\s-]/g, ""),
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
      });
      setBooked(result);
    } catch (err) {
      const status = err?.response?.status;
      setError(getSchedulingErrorMessage(err, "Could not book. Please try again."));
      if (status === 409) {
        await pickDate(date);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">Booking unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">{loadError}</p>
        </div>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#096dea] border-t-transparent" />
      </div>
    );
  }

  const tz = info.timezone;
  const service = info.services.find((s) => s.id === serviceId);
  const provider = info.providers.find((p) => p.id === providerId);
  const summary = [service?.name || "Appointment", provider ? `with ${provider.name}` : null].filter(Boolean).join(" ");

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-lg space-y-6">
        <header className="text-center">
          <CalendarCheck2 className="mx-auto text-[#096dea]" size={32} />
          <h1 className="mt-2 text-xl font-bold text-slate-900">{info.bot_name}</h1>
          <p className="text-sm text-slate-500">Book an appointment</p>
        </header>

        <main className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">{error}</div>}

          {booked ? (
            <div className="space-y-3 py-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-2xl">✓</div>
              <h2 className="text-lg font-bold text-slate-900">Appointment confirmed</h2>
              <p className="text-sm text-slate-700">
                {booked.service_name || "Appointment"} with {booked.provider_name}
                <br />
                {formatDateTime(booked.start_at, booked.timezone)}
              </p>
              <p className="text-xs text-slate-500">Booking ID #{booked.id}. Please arrive a few minutes early.</p>
            </div>
          ) : step === "service" ? (
            <Step title="Choose a service">
              <div className="space-y-2">
                {info.services.map((s) => (
                  <Choice
                    key={s.id}
                    selected={serviceId === s.id}
                    onClick={() => { setServiceId(s.id); setProviderId(ANY); setStep("provider"); }}
                    title={s.name}
                    subtitle={`${s.duration_minutes} min${s.price != null ? ` · ₹${s.price}` : ""}`}
                  />
                ))}
              </div>
            </Step>
          ) : step === "provider" ? (
            <Step title="Choose who" onBack={info.services.length ? () => setStep("service") : null}>
              <div className="space-y-2">
                {info.providers.length > 1 && (
                  <Choice selected={providerId === ANY} onClick={() => { setProviderId(ANY); goToDates(); }} title="Any available" subtitle="Earliest free time" />
                )}
                {info.providers.map((p) => (
                  <Choice key={p.id} selected={providerId === p.id} onClick={() => { setProviderId(p.id); goToDates(); }} title={p.name} subtitle={p.title} />
                ))}
                {info.providers.length === 0 && <p className="text-sm text-slate-500">No one is taking bookings right now.</p>}
              </div>
            </Step>
          ) : step === "date" ? (
            <Step title="Choose a date" onBack={() => setStep("provider")}>
              <p className="text-xs text-slate-500">{summary}</p>
              {dates === null ? (
                <p className="text-sm text-slate-500">Finding free dates…</p>
              ) : dates.length === 0 ? (
                <p className="text-sm text-slate-500">No free dates in the next {info.max_days_ahead} days. Please try another option.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {dates.map((d) => (
                    <Choice key={d.date} selected={date === d.date} onClick={() => pickDate(d.date)} title={formatDate(d.date)} subtitle={`${d.slot_count} slots`} />
                  ))}
                </div>
              )}
            </Step>
          ) : step === "time" ? (
            <Step title="Choose a time" onBack={() => setStep("date")}>
              <p className="text-xs text-slate-500">{summary} · {formatDate(date)}</p>
              {slots === null ? (
                <p className="text-sm text-slate-500">Loading times…</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-slate-500">This day just filled up. Please go back and pick another date.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {slots.map((s) => (
                    <button
                      key={s.start_at}
                      type="button"
                      onClick={() => { setSlot(s.start_at); setStep("details"); }}
                      className={`rounded-xl border px-2 py-2 text-sm font-semibold ${slot === s.start_at ? "border-[#096dea] bg-[#096dea] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                    >
                      {formatTime(s.start_at, tz)}
                    </button>
                  ))}
                </div>
              )}
            </Step>
          ) : (
            <Step title="Your details" onBack={() => setStep("time")}>
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                {summary}
                <br />
                <b>{formatDateTime(slot, tz)}</b>
              </div>
              <form className="space-y-3" onSubmit={submit}>
                {[
                  ["name", "Name", "text", true, "Patient / client name"],
                  ["phone", "Mobile number (with country code)", "tel", true, "919876543210"],
                  ["email", "Email (optional)", "email", false, ""],
                ].map(([key, label, type, required, placeholder]) => (
                  <label key={key} className="block text-xs font-semibold text-slate-600">
                    {label}
                    <input
                      type={type}
                      required={required}
                      placeholder={placeholder}
                      value={form[key]}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-[#096dea] focus:outline-none focus:ring-1 focus:ring-[#096dea]"
                    />
                  </label>
                ))}
                <label className="block text-xs font-semibold text-slate-600">
                  Notes (optional)
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#096dea] focus:outline-none"
                  />
                </label>
                <button type="submit" disabled={submitting} className="w-full rounded-xl bg-[#096dea] py-3 text-sm font-bold text-white hover:bg-[#0959bb] disabled:opacity-50">
                  {submitting ? "Booking…" : "Confirm booking"}
                </button>
              </form>
            </Step>
          )}
        </main>
        <p className="text-center text-[11px] text-slate-400">Times shown in {tz}</p>
      </div>
    </div>
  );
}
